import { Link } from '@tanstack/react-router';
import {
    Cloud,
    CloudFog,
    CloudLightning,
    CloudRain,
    CloudSnow,
    Droplets,
    Sun,
    Thermometer,
    Waves,
    Wind,
} from 'lucide-react';

import type { SeaForecastPeriod, Weather, WeatherForecastDay } from '@/lib/content-types';
import { feelsLikeCelsius } from '@/lib/feels-like';
import { PRIMARY_WEATHER_CITY } from '@/lib/weather-cities';
import { cn } from '@/lib/utils';

export function WeatherGlyph({
    iconClass,
    className,
}: {
    iconClass?: string;
    className?: string;
}) {
    const cls = cn('size-3.5 shrink-0', className);
    const key = iconClass ?? '';
    if (/rain|sleet|hail|raindrop/i.test(key)) return <CloudRain className={cls} aria-hidden />;
    if (/snow/i.test(key)) return <CloudSnow className={cls} aria-hidden />;
    if (/lightning/i.test(key)) return <CloudLightning className={cls} aria-hidden />;
    if (/fog|haze|smog/i.test(key)) return <CloudFog className={cls} aria-hidden />;
    if (/wind|sandstorm|strong-wind/i.test(key)) return <Wind className={cls} aria-hidden />;
    if (/cloud|cloudy/i.test(key)) return <Cloud className={cls} aria-hidden />;
    return <Sun className={cls} aria-hidden />;
}

function jerusalemDateParts(now = new Date()): { weekday: number; dd: string; mm: string; yy: string } {
    const dateKey = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
    const [year, month, day] = dateKey.split('-').map(Number);
    const weekday = new Date(Date.UTC(year!, month! - 1, day!, 12, 0, 0)).getUTCDay();
    return {
        weekday,
        dd: String(day).padStart(2, '0'),
        mm: String(month).padStart(2, '0'),
        yy: String(year).slice(-2),
    };
}

function formatHeaderDate(now = new Date()): string {
    const dayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    const { weekday, dd, mm, yy } = jerusalemDateParts(now);
    const day = dayNames[weekday] ?? '';
    return `יום ${day} ה-${dd}.${mm}.${yy}`;
}

export function formatTempRange(
    min?: number | null,
    max?: number | null,
    fallback?: number | null,
): string | null {
    if (min != null && max != null) return `${Math.round(min)}°-${Math.round(max)}°`;
    if (fallback != null) return `${Math.round(fallback)}°`;
    if (min != null) return `${Math.round(min)}°`;
    if (max != null) return `${Math.round(max)}°`;
    return null;
}

function formatTempLabel(weather: Weather): string | null {
    return formatTempRange(weather.tempMin, weather.tempMax, weather.temp);
}

/** True when there is enough weather data to render the homepage strip. */
export function hasWeatherDisplay(weather: Weather | null | undefined): boolean {
    if (!weather) return false;
    return Boolean(formatTempLabel(weather) || weather.description);
}

export function hasSeaDisplay(sea?: SeaForecastPeriod[] | null): boolean {
    return Boolean(sea && sea.length > 0);
}

function currentSeaPeriod(sea?: SeaForecastPeriod[] | null): SeaForecastPeriod | null {
    if (!sea?.length) return null;
    const now = Date.now();
    return (
        sea.find((period) => {
            const from = new Date(period.validFrom).getTime();
            const to = new Date(period.validTo).getTime();
            return from <= now && now <= to;
        })
        ?? sea[0]
        ?? null
    );
}

function formatWaveRange(from?: number | null, to?: number | null): string | null {
    if (from != null && to != null && from !== to) return `גלים ${from}-${to} ס״מ`;
    if (from != null) return `גלים ${from} ס״מ`;
    if (to != null) return `גלים ${to} ס״מ`;
    return null;
}

function formatWindRange(period: SeaForecastPeriod): string | null {
    const fromDir = windDirectionLabel(period.windDirFrom);
    const toDir = windDirectionLabel(period.windDirTo);
    let dir: string | null = null;
    if (fromDir && toDir && fromDir !== toDir) dir = `${fromDir} עד ${toDir}`;
    else dir = fromDir ?? toDir;

    const from = period.windSpeedFrom;
    const to = period.windSpeedTo;
    let speed: string | null = null;
    if (from != null && to != null && from !== to) speed = `${from}-${to} קמ״ש`;
    else if (from != null) speed = `${from} קמ״ש`;
    else if (to != null) speed = `${to} קמ״ש`;

    if (dir && speed) return `רוח ${dir} ${speed}`;
    if (speed) return `רוח ${speed}`;
    if (dir) return `רוח ${dir}`;
    return null;
}

function jerusalemClock(iso: string): { date: string; time: string } | null {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Jerusalem',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        })
            .formatToParts(d)
            .map((p) => [p.type, p.value]),
    );
    return {
        date: `${parts.year}-${parts.month}-${parts.day}`,
        time: `${parts.hour}:${parts.minute}`,
    };
}

function seaPeriodTitle(period: SeaForecastPeriod): string {
    const from = jerusalemClock(period.validFrom);
    const to = jerusalemClock(period.validTo);
    const range = from && to ? `${from.time}-${to.time}` : '';
    const today = jerusalemTodayKey();
    const tomorrow = addDaysKey(today, 1);
    if (from && to) {
        if (from.date === today && to.date === tomorrow) {
            return range ? `הלילה · ${range}` : 'הלילה';
        }
        if (from.date === today) return range ? `היום · ${range}` : 'היום';
        if (from.date === tomorrow) return range ? `מחר · ${range}` : 'מחר';
        return range ? `${shortHebrewDate(from.date)} · ${range}` : shortHebrewDate(from.date);
    }
    return 'תחזית ים';
}

function jerusalemTodayKey(): string {
    const jerusalem = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' }),
    );
    const y = jerusalem.getFullYear();
    const m = String(jerusalem.getMonth() + 1).padStart(2, '0');
    const d = String(jerusalem.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function addDaysKey(dateKey: string, days: number): string {
    const d = new Date(`${dateKey}T12:00:00`);
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function hebrewWeekday(dateStr: string): string {
    const dayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    const d = new Date(`${dateStr}T12:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return dayNames[d.getDay()] ?? dateStr;
}

function shortHebrewDate(dateStr: string): string {
    const d = new Date(`${dateStr}T12:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}`;
}

function dayTitle(dateStr: string): string {
    const today = jerusalemTodayKey();
    if (dateStr === today) return 'היום';
    if (dateStr === addDaysKey(today, 1)) return 'מחר';
    return `יום ${hebrewWeekday(dateStr)}`;
}

function upcomingDays(weather: Weather, limit = 5): WeatherForecastDay[] {
    const todayKey = jerusalemTodayKey();
    return weather.forecastDays
        .filter((day) => day.date >= todayKey)
        .filter((day) => day.tempMin != null || day.tempMax != null || day.description)
        .slice(0, limit);
}

function historyDays(weather: Weather, limit = 7): WeatherForecastDay[] {
    return weather.historyDays
        .filter((day) => day.tempMin != null || day.tempMax != null || day.description)
        .slice(0, limit);
}

/** Map IMS wind degrees to a short Hebrew direction. */
function windDirectionLabel(deg?: number | null): string | null {
    if (deg == null || !Number.isFinite(deg)) return null;
    const normalized = ((deg % 360) + 360) % 360;
    const sectors = [
        { max: 22.5, label: 'צפונית' },
        { max: 67.5, label: 'צפון-מזרחית' },
        { max: 112.5, label: 'מזרחית' },
        { max: 157.5, label: 'דרום-מזרחית' },
        { max: 202.5, label: 'דרומית' },
        { max: 247.5, label: 'דרום-מערבית' },
        { max: 292.5, label: 'מערבית' },
        { max: 337.5, label: 'צפון-מערבית' },
        { max: 360, label: 'צפונית' },
    ];
    return sectors.find((s) => normalized <= s.max)?.label ?? 'צפונית';
}

function formatObservedAt(iso?: string): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('he-IL', {
        timeZone: 'Asia/Jerusalem',
        hour: '2-digit',
        minute: '2-digit',
    }).format(d);
}

function cityHighlightTemp(weather: Weather): number | null {
    if (weather.tempMax != null) return weather.tempMax;
    if (weather.temp != null) return weather.temp;
    if (weather.tempMin != null) return weather.tempMin;
    return null;
}

/**
 * Dense weather strip for the homepage main column - grid seam, not a card.
 */
export function HomeWeatherStrip({
    weather,
    sea,
    className,
}: {
    weather: Weather | null | undefined;
    sea?: SeaForecastPeriod[] | null;
    className?: string;
}) {
    const showWeather = hasWeatherDisplay(weather);
    const period = currentSeaPeriod(sea);
    const seaText = period
        ? [period.seaStateLabel, formatWaveRange(period.waveHeightFrom, period.waveHeightTo)]
            .filter(Boolean)
            .join(' · ')
        : '';
    if ((!showWeather || !weather) && !seaText) return null;

    const tempPart = weather ? formatTempLabel(weather) : null;
    const condition = [tempPart, weather?.description].filter(Boolean).join(' ');

    const feelsLike = weather
        ? feelsLikeCelsius(weather.temp, weather.humidity, weather.windSpeed)
        : null;

    const meta: { key: string; icon: React.ReactNode; text: string }[] = [];
    if (feelsLike != null) {
        meta.push({
            key: 'feels',
            icon: <Thermometer className="size-3.5 shrink-0" aria-hidden />,
            text: `מרגיש כמו ${Math.round(feelsLike)}°`,
        });
    }
    if (weather?.humidity != null) {
        meta.push({
            key: 'humidity',
            icon: <Droplets className="size-3.5 shrink-0" aria-hidden />,
            text: `לחות ${Math.round(weather.humidity)}%`,
        });
    }
    if (weather?.windSpeed != null) {
        meta.push({
            key: 'wind',
            icon: <Wind className="size-3.5 shrink-0" aria-hidden />,
            text: `רוח ${Math.round(weather.windSpeed)} קמ״ש`,
        });
    }
    if (seaText) {
        meta.push({
            key: 'sea',
            icon: <Waves className="size-3.5 shrink-0" aria-hidden />,
            text: seaText,
        });
    }

    return (
        <Link
            to="/weather"
            className={cn(
                'group flex flex-col gap-2 border-b border-white/10 px-6 py-3 text-sm text-white/55',
                'sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1.5',
                className,
            )}
        >
            <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                {condition ? (
                    <span className="inline-flex min-w-0 items-center gap-1.5 text-white/70 transition-colors group-hover:text-white">
                        <WeatherGlyph iconClass={weather?.iconClass} />
                        <span className="min-w-0">{condition}</span>
                    </span>
                ) : (
                    <span className="inline-flex min-w-0 items-center gap-1.5 text-white/70 transition-colors group-hover:text-white">
                        <Waves className="size-3.5 shrink-0" aria-hidden />
                        <span className="min-w-0">מצב הים</span>
                    </span>
                )}
            </div>

            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1">
                <span className="text-white/55">{formatHeaderDate()}</span>
                {meta.map((item) => (
                    <span
                        key={item.key}
                        className="inline-flex items-center gap-1.5 text-white/55"
                    >
                        <span aria-hidden className="text-white/20">
                            ·
                        </span>
                        {item.icon}
                        {item.text}
                    </span>
                ))}
                <span className="ms-auto hidden text-sm text-white/55 transition-colors group-hover:text-white sm:inline">
                    תחזית מלאה
                </span>
            </div>
        </Link>
    );
}

function ForecastDayCell({
    day,
    highlight,
}: {
    day: WeatherForecastDay;
    highlight?: boolean;
}) {
    const range = formatTempRange(day.tempMin, day.tempMax);
    return (
        <div
            className={cn(
                'flex min-w-[7.5rem] flex-1 flex-col gap-2 border-e border-white/10 px-4 py-5 last:border-e-0',
                highlight && 'bg-white/[0.02]',
            )}
        >
            <p className="text-sm text-white/55">{dayTitle(day.date)}</p>
            <p className="text-xs text-white/55">{shortHebrewDate(day.date)}</p>
            <div className="mt-1 flex items-center gap-2 text-white">
                <WeatherGlyph iconClass={day.iconClass} className="size-4" />
                <span className="text-lg tracking-tight">{range ?? '—'}</span>
            </div>
            {(day.tempMin != null || day.tempMax != null) && (
                <p className="text-xs text-white/55">
                    {[
                        day.tempMin != null ? `מינ׳ ${Math.round(day.tempMin)}°` : null,
                        day.tempMax != null ? `מקס׳ ${Math.round(day.tempMax)}°` : null,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </p>
            )}
            {day.description && (
                <p className="text-sm leading-snug text-white/55">{day.description}</p>
            )}
        </div>
    );
}

function CitySnippet({
    weather,
    className,
}: {
    weather: Weather;
    className?: string;
}) {
    const range = formatTempLabel(weather);
    // Skip today in the secondary line - the header already covers it.
    const todayKey = jerusalemTodayKey();
    const days = upcomingDays(weather, 4).filter((day) => day.date !== todayKey).slice(0, 3);

    return (
        <div className={cn('px-5 py-6 text-start sm:px-6', className)}>
            <p className="text-base text-white">{weather.locationName}</p>
            <p className="mt-2 inline-flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                {range && <span className="text-white">{range}</span>}
                {weather.description && (
                    <span className="text-white/50">{weather.description}</span>
                )}
                {!range && !weather.description && (
                    <span className="text-white/50">אין נתונים</span>
                )}
            </p>
            {days.length > 0 && (
                <dl className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                    {days.map((day) => {
                        const title = dayTitle(day.date);
                        const label =
                            title === 'מחר' ? title : hebrewWeekday(day.date);
                        const high =
                            day.tempMax != null
                                ? `${Math.round(day.tempMax)}°`
                                : formatTempRange(day.tempMin, day.tempMax);
                        if (!high) return null;
                        return (
                            <div key={day.date} className="flex items-baseline gap-1.5">
                                <dt className="text-white/55">{label}</dt>
                                <dd className="text-white/75">{high}</dd>
                            </div>
                        );
                    })}
                </dl>
            )}
        </div>
    );
}

function ObservationMeta({ weather }: { weather: Weather }) {
    const windDir = windDirectionLabel(weather.windDirectionDeg);
    const observed = formatObservedAt(weather.observedAt);
    const feelsLike = feelsLikeCelsius(weather.temp, weather.humidity, weather.windSpeed);
    const items: { key: string; icon: React.ReactNode; text: string }[] = [];

    if (weather.temp != null) {
        items.push({
            key: 'temp',
            icon: <Thermometer className="size-3.5 shrink-0" aria-hidden />,
            text: `מדידה נוכחית ${Math.round(weather.temp)}°`,
        });
    }
    if (feelsLike != null) {
        items.push({
            key: 'feels',
            icon: <Thermometer className="size-3.5 shrink-0" aria-hidden />,
            text: `מרגיש כמו ${Math.round(feelsLike)}°`,
        });
    }
    if (weather.humidity != null) {
        items.push({
            key: 'humidity',
            icon: <Droplets className="size-3.5 shrink-0" aria-hidden />,
            text: `לחות ${Math.round(weather.humidity)}%`,
        });
    }
    if (weather.windSpeed != null) {
        items.push({
            key: 'wind',
            icon: <Wind className="size-3.5 shrink-0" aria-hidden />,
            text: windDir
                ? `רוח ${windDir} ${Math.round(weather.windSpeed)} קמ״ש`
                : `רוח ${Math.round(weather.windSpeed)} קמ״ש`,
        });
    }
    if (weather.rain != null) {
        items.push({
            key: 'rain',
            icon: <CloudRain className="size-3.5 shrink-0" aria-hidden />,
            text:
                weather.rain > 0
                    ? `גשם ${weather.rain.toFixed(1)} מ״מ`
                    : 'ללא גשם',
        });
    }
    if (observed) {
        items.push({
            key: 'updated',
            icon: null,
            text: `עודכן ב-${observed}`,
        });
    }

    if (items.length === 0) return null;

    return (
        <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/55">
            {items.map((item) => (
                <span key={item.key} className="inline-flex items-center gap-1.5">
                    {item.icon}
                    {item.text}
                </span>
            ))}
            <span>{formatHeaderDate()}</span>
        </div>
    );
}

function SeaForecastSection({ sea }: { sea: SeaForecastPeriod[] }) {
    if (sea.length === 0) return null;

    return (
        <section aria-label="מצב הים">
            <div className="border-t border-white/10 px-6 py-3">
                <p className="text-sm text-white/55">מצב הים · חוף דרום</p>
            </div>
            <div className="flex overflow-x-auto border-t border-white/10">
                {sea.map((period, index) => {
                    const waves = formatWaveRange(period.waveHeightFrom, period.waveHeightTo);
                    const wind = formatWindRange(period);
                    const extras = [
                        period.seaTemp != null ? `מי הים ${Math.round(period.seaTemp)}°` : null,
                        period.heatStressLabel ? `עומס חום ${period.heatStressLabel}` : null,
                        period.uvLabel ? `קרינה ${period.uvLabel}` : null,
                    ].filter(Boolean);

                    return (
                        <div
                            key={period.$id}
                            className={cn(
                                'flex min-w-[11rem] flex-1 flex-col gap-2 border-e border-white/10 px-4 py-5 last:border-e-0',
                                index === 0 && 'bg-white/[0.02]',
                            )}
                        >
                            <p className="text-sm text-white/55">{seaPeriodTitle(period)}</p>
                            <div className="mt-1 flex items-center gap-2 text-white">
                                <Waves className="size-4 shrink-0" aria-hidden />
                                <span className="text-lg tracking-tight">
                                    {period.seaStateLabel ?? '—'}
                                </span>
                            </div>
                            {waves && (
                                <p className="text-sm text-white/70">{waves}</p>
                            )}
                            {wind && (
                                <p className="text-sm leading-snug text-white/55">{wind}</p>
                            )}
                            {period.description && (
                                <p className="text-sm text-white/55">{period.description}</p>
                            )}
                            {extras.length > 0 && (
                                <p className="text-xs leading-relaxed text-white/55">
                                    {extras.join(' · ')}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
            <p className="border-t border-white/10 px-6 py-3 text-xs leading-relaxed text-white/55">
                תחזית אזורית לחוף הדרום. צבע הדגל נקבע בחוף לפי המציל, ולא לפי התחזית הזו.
            </p>
        </section>
    );
}

function ExtremesStrip({ cities }: { cities: Weather[] }) {
    const ranked = cities
        .map((city) => ({ city, temp: cityHighlightTemp(city) }))
        .filter((row): row is { city: Weather; temp: number } => row.temp != null)
        .sort((a, b) => b.temp - a.temp);

    if (ranked.length < 2) return null;

    const hottest = ranked[0];
    const coldest = ranked[ranked.length - 1];
    if (hottest.city.locationKey === coldest.city.locationKey) return null;

    return (
        <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 px-6 py-4 text-sm text-white/55">
            <span>
                הכי חם:{' '}
                <span className="text-white">
                    {hottest.city.locationName} {Math.round(hottest.temp)}°
                </span>
            </span>
            <span aria-hidden className="hidden text-white/20 sm:inline">
                /
            </span>
            <span>
                הכי קריר:{' '}
                <span className="text-white">
                    {coldest.city.locationName} {Math.round(coldest.temp)}°
                </span>
            </span>
        </div>
    );
}

/**
 * Full /weather page composition: Ashkelon hero + multi-day forecast + city snippets.
 */
export function WeatherPageView({
    primary,
    cities,
    sea = [],
}: {
    primary: Weather | null;
    cities: Weather[];
    sea?: SeaForecastPeriod[];
}) {
    const others = cities.filter((c) => c.locationKey !== (primary?.locationKey ?? PRIMARY_WEATHER_CITY));
    const days = primary ? upcomingDays(primary, 5) : [];
    const past = primary ? historyDays(primary, 7) : [];
    const feelsLike = primary
        ? feelsLikeCelsius(primary.temp, primary.humidity, primary.windSpeed)
        : null;

    return (
        <>
            <section className="relative px-6 py-12 md:py-16">
                <p className="text-sm text-white/55">מזג האוויר</p>
                <h1 className="mt-3 text-4xl font-normal tracking-[-0.04em] md:text-5xl">
                    {primary?.locationName ?? 'אשקלון'}
                </h1>
                {primary && hasWeatherDisplay(primary) ? (
                    <div className="mt-6 flex flex-wrap items-end gap-x-6 gap-y-3">
                        <div className="inline-flex items-center gap-3 text-white">
                            <WeatherGlyph
                                iconClass={primary.iconClass}
                                className="size-7"
                            />
                            <span className="text-3xl tracking-tight md:text-4xl">
                                {formatTempLabel(primary)}
                            </span>
                        </div>
                        {primary.description && (
                            <p className="text-lg text-white/55">{primary.description}</p>
                        )}
                        {feelsLike != null && (
                            <p className="text-base text-white/55">
                                מרגיש כמו {Math.round(feelsLike)}°
                            </p>
                        )}
                    </div>
                ) : (
                    <p className="mt-6 text-base text-white/55">
                        אין כרגע נתוני מזג אוויר לעדכון.
                    </p>
                )}

                {primary && <ObservationMeta weather={primary} />}
            </section>

            <SeaForecastSection sea={sea} />

            {days.length > 0 && (
                <section aria-label="תחזית לימים הקרובים">
                    <div className="border-t border-white/10 px-6 py-3">
                        <p className="text-sm text-white/55">תחזית לימים הקרובים</p>
                    </div>
                    <div className="flex overflow-x-auto border-t border-white/10">
                        {days.map((day, index) => (
                            <ForecastDayCell
                                key={day.date}
                                day={day}
                                highlight={index === 0}
                            />
                        ))}
                    </div>
                </section>
            )}

            {past.length > 0 && (
                <section aria-label="ימים קודמים">
                    <div className="border-t border-white/10 px-6 py-3">
                        <p className="text-sm text-white/55">ימים קודמים</p>
                    </div>
                    <div className="flex overflow-x-auto border-t border-white/10">
                        {past.map((day) => (
                            <ForecastDayCell key={day.date} day={day} />
                        ))}
                    </div>
                </section>
            )}

            {others.length > 0 && (
                <section aria-label="ערים נוספות">
                    <div className="border-t border-white/10 px-6 py-3">
                        <p className="text-sm text-white/55">ערים נוספות</p>
                    </div>
                    <ExtremesStrip cities={others} />
                    <div className="grid grid-cols-1 border-t border-white/10 sm:grid-cols-2 lg:grid-cols-3">
                        {others.map((city) => (
                            <CitySnippet
                                key={city.locationKey}
                                weather={city}
                                className={cn(
                                    'border-b border-white/10',
                                    'sm:border-e sm:max-lg:[&:nth-child(2n)]:border-e-0',
                                    'lg:[&:nth-child(3n)]:border-e-0',
                                )}
                            />
                        ))}
                    </div>
                </section>
            )}

            <p className="border-t border-white/10 px-6 py-5 text-xs leading-relaxed text-white/55">
                הנתונים מבוססים על השירות המטאורולוגי הישראלי. תחזית הערים מתעדכנת פעם ביום.
                מצב הים הוא תחזית לחוף הדרום, לא דיווח מהמציל בחוף.
            </p>
        </>
    );
}
